import React, { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaMapMarkerAlt, FaFileAlt, FaHeart } from "react-icons/fa";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const API_BASE_URL = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";

export default function TourAndTravelPage() {
  const [countries, setCountries] = useState([]);
  const [packages, setPackages] = useState([]);
  
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [filteredCities, setFilteredCities] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  
  const user = useSelector((state) => state.auth?.user);

  useEffect(() => {
    const fetchPublicData = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/tour/public`);
        setCountries(res.data.countries || []);
        setPackages(res.data.packages || []);
      } catch (err) {
        console.error("Failed to fetch tour data", err);
      }
    };
    fetchPublicData();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      // Find all unique cities for the selected country
      const pkgsForCountry = packages.filter(p => p.tourCountryId?._id === selectedCountry);
      const uniqueCities = [...new Set(pkgsForCountry.map(p => p.cityName))];
      setFilteredCities(uniqueCities);
      setSelectedCity(""); // Reset city when country changes
      setFilteredPackages([]);
    } else {
      setFilteredCities([]);
      setSelectedCity("");
      setFilteredPackages([]);
    }
  }, [selectedCountry, packages]);

  useEffect(() => {
    if (selectedCountry && selectedCity) {
      const pkgs = packages.filter(p => p.tourCountryId?._id === selectedCountry && p.cityName === selectedCity);
      setFilteredPackages(pkgs);
    }
  }, [selectedCountry, selectedCity, packages]);

  const handleInterest = async (packageId) => {
    if (!user) {
      toast.error("Please login to show interest");
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/tour/inquiries`, { tourPackageId: packageId }, { withCredentials: true });
      toast.success("Interest sent successfully! Admin will review it.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to submit interest");
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-blue-900 text-white py-20 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Discover Your Next Adventure</h1>
        <p className="text-lg opacity-90 max-w-2xl mx-auto">Explore exclusive Tour & Travel packages around the globe. Select a destination to see available plans.</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 flex flex-col md:flex-row gap-6 mb-12">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Country</label>
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 bg-gray-50"
            >
              <option value="">-- Choose Country --</option>
              {countries.map(c => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select City</label>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              disabled={!selectedCountry || filteredCities.length === 0}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 bg-gray-50 disabled:opacity-50"
            >
              <option value="">-- Choose City --</option>
              {filteredCities.map((city, i) => (
                <option key={i} value={city}>{city}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Packages List */}
        {selectedCity ? (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaMapMarkerAlt className="text-blue-600 mr-2" /> Packages in {selectedCity}
            </h2>
            {filteredPackages.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl shadow-sm text-center border">
                <p className="text-gray-500 text-lg">No packages found for this city.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPackages.map(pkg => (
                  <div key={pkg._id} className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-shadow flex flex-col h-full">
                    <div className="p-6 flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.title}</h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">{pkg.shortDescription}</p>
                    </div>
                    <div className="p-6 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-4">
                      {pkg.documentUrl ? (
                        <a
                          href={pkg.documentUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 border border-gray-300 px-4 py-2 rounded-lg bg-white"
                        >
                          <FaFileAlt className="mr-2" /> View Doc
                        </a>
                      ) : <div className="text-sm text-gray-400 italic">No doc available</div>}
                      <button
                        onClick={() => handleInterest(pkg._id)}
                        className="flex-1 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                      >
                        <FaHeart className="mr-2" /> Interest
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-4">
              <FaMapMarkerAlt className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700">Please select a Country and City</h3>
            <p className="text-gray-500 mt-2">to view available tour packages</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
